import React, { useState } from "react";

import "./artifacts.css";

// Each step maps to one stage of the PROBE method. A principle you can answer
// "Yes" to on all five is measurable. Anything less is still partly a slogan.
const STEPS = [
	{
		label: "Name it precisely",
		q: "Can you state the principle in one sentence, with no room to argue what it means? \"Be fair\" fails. \"Treat two users the same when the only difference is their name\" passes.",
	},
	{
		label: "Define the violation",
		q: "Can you describe the exact behavior you would call a failure? If you cannot picture the bad output, you cannot catch it.",
	},
	{
		label: "Design a probe",
		q: "Could you write concrete test inputs that would expose that failure? For fairness, matched messages identical except for a name.",
	},
	{
		label: "Set a metric and a line",
		q: "Could a rater turn each result into a score from 0 to 1, and would you commit to a pass threshold before you look at the numbers?",
	},
	{
		label: "Keep the evidence",
		q: "Would you save the worst examples, side by side, so the score is never the whole story?",
	},
];

const OPTIONS = [
	{ label: "No", score: 0 },
	{ label: "Partly", score: 1 },
	{ label: "Yes", score: 2 },
];

function verdict(score) {
	if (score >= 9) return { name: "Measurable", color: "#2f855a" };
	if (score >= 5) return { name: "Half a slogan", color: "#dd6b20" };
	return { name: "Still a slogan", color: "#c53030" };
}

const PrincipleMeasurabilityTest = () => {
	const [answers, setAnswers] = useState({});

	const complete = Object.keys(answers).length === STEPS.length;
	const score = Object.values(answers).reduce((a, b) => a + b, 0);
	const v = verdict(score);
	const weak = STEPS.filter((s, i) => answers[i] < 2);

	return (
		<div className="artifact-card">
			<div className="artifact-kicker">Interactive · Free tool</div>
			<div className="artifact-title">
				Can you measure that AI principle?
			</div>
			<div className="artifact-intro">
				Pick a principle you care about for an AI system, then answer for
				it. These are the five steps PROBE uses to turn a principle into a
				score. If you cannot answer "Yes" to all five, you have a slogan,
				not a measurement.
			</div>

			{STEPS.map((s, i) => (
				<div className="artifact-question" key={i}>
					<div className="artifact-q-text">
						{i + 1}. {s.label}. {s.q}
					</div>
					<div className="artifact-options">
						{OPTIONS.map((opt) => (
							<div
								key={opt.label}
								className={
									"artifact-option" +
									(answers[i] === opt.score
										? " selected"
										: "")
								}
								onClick={() =>
									setAnswers((prev) => ({
										...prev,
										[i]: opt.score,
									}))
								}
							>
								{opt.label}
							</div>
						))}
					</div>
				</div>
			))}

			{complete && (
				<div className="artifact-result">
					<div className="artifact-score-row">
						<span
							className="artifact-tier"
							style={{ color: v.color, fontSize: "22px" }}
						>
							{v.name}
						</span>
						<span
							className="artifact-readout"
							style={{ marginLeft: "10px" }}
						>
							{score} / 10
						</span>
					</div>
					{weak.length > 0 ? (
						<div className="artifact-readout">
							The steps still holding you back:
							<ul className="artifact-gaps">
								{weak.map((s, i) => (
									<li key={i}>{s.label}</li>
								))}
							</ul>
							Close these and the principle becomes something you
							can actually test.
						</div>
					) : (
						<div className="artifact-readout">
							Five clean "Yes" answers. This principle is
							measurable. See it scored live at
							framework.swapniltamse.com.
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default PrincipleMeasurabilityTest;
